import React from 'react';
import {
  TrendingUp,
  Target,
  BarChart3,
  Lightbulb,
  Sparkles
} from 'lucide-react';

interface FormattedSection {
  title: string;
  content: string;
  type: 'analysis' | 'conclusion' | 'recommendation' | 'insight';
  icon: React.ComponentType<any>;
  gradient: string;
  bgGradient: string;
}

export const parseAIAnalysis = (analysisText: string): FormattedSection[] => {
  if (!analysisText || analysisText.trim().length === 0) return [];

  // Clean the text and split into sections
  const cleanText = analysisText.trim();
  const sections: FormattedSection[] = [];

  // Enhanced patterns to match Spanish analysis headers
  const sectionPatterns = [
    {
      pattern: /(?:1\.|^)[\s]*(?:an[aá]lisis[\s]+t[eé]cnico|t[eé]cnico[\s]*-?[\s]*t[aá]ctico)/i,
      type: 'analysis' as const,
      title: 'Análisis Técnico-Táctico',
      icon: BarChart3,
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100'
    },
    {
      pattern: /(?:2\.|^)[\s]*(?:relaci[oó]n[\s]+valor|valor[\s]*\/[\s]*rendimiento|valor[\s]+de[\s]+mercado)/i,
      type: 'conclusion' as const,
      title: 'Relación Valor/Rendimiento',
      icon: TrendingUp,
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100'
    },
    {
      pattern: /(?:3\.|^)[\s]*(?:recomendaciones[\s]+estrat[eé]gicas|estrategia|fichajes)/i,
      type: 'recommendation' as const,
      title: 'Recomendaciones Estratégicas',
      icon: Target,
      gradient: 'from-purple-500 to-purple-600',
      bgGradient: 'from-purple-50 to-purple-100'
    },
    {
      pattern: /(?:4\.|^)[\s]*(?:insights|m[eé]tricas[\s]+avanzadas|datos[\s]+avanzados|xg|xa)/i,
      type: 'insight' as const,
      title: 'Insights de Métricas Avanzadas',
      icon: Lightbulb,
      gradient: 'from-orange-500 to-orange-600',
      bgGradient: 'from-orange-50 to-orange-100'
    }
  ];

  // Split by numbered sections first (1., 2., 3., 4.)
  const numberedSections = cleanText.split(/(?=\n?\d+\.\s*[A-ZÁÉÍÓÚ])/);

  if (numberedSections.length > 1) {
    // We have numbered sections
    numberedSections.forEach((section) => {
      const trimmedSection = section.trim();
      if (!trimmedSection || trimmedSection.length < 10) return;

      // Find matching pattern
      const matchingPattern = sectionPatterns.find(pattern =>
        pattern.pattern.test(trimmedSection)
      );

      if (matchingPattern) {
        // Extract content - remove the number and title line
        const lines = trimmedSection.split('\n');
        const contentLines = lines.slice(1).filter(line => line.trim().length > 0);
        const content = contentLines.join('\n').trim();

        if (content.length > 0) {
          sections.push({
            title: matchingPattern.title,
            content: content,
            type: matchingPattern.type,
            icon: matchingPattern.icon,
            gradient: matchingPattern.gradient,
            bgGradient: matchingPattern.bgGradient
          });
        }
      }
    });
  }

  // If we didn't find numbered sections or found less than 2, try paragraph split
  if (sections.length < 2) {
    sections.length = 0; // Clear any partial results

    const paragraphs = cleanText.split(/\n\s*\n/).filter(p => p.trim().length > 20);

    if (paragraphs.length >= 2) {
      paragraphs.forEach((paragraph, index) => {
        const trimmedParagraph = paragraph.trim();

        // Try to match with patterns
        const matchingPattern = sectionPatterns.find(pattern =>
          pattern.pattern.test(trimmedParagraph)
        ) || sectionPatterns[index % sectionPatterns.length]; // Fallback to cycling through patterns

        // Extract title if present in first line
        const lines = trimmedParagraph.split('\n');
        let title = matchingPattern.title;
        let content = trimmedParagraph;

        if (lines.length > 1 && lines[0].length < 100 && /^[\dA-ZÁÉÍÓÚ\s\-\/]+$/.test(lines[0])) {
          title = lines[0].replace(/^\d+\.\s*/, '').trim();
          content = lines.slice(1).join('\n').trim();
        }

        sections.push({
          title: title,
          content: content,
          type: matchingPattern.type,
          icon: matchingPattern.icon,
          gradient: matchingPattern.gradient,
          bgGradient: matchingPattern.bgGradient
        });
      });
    }
  }

  // Final fallback: return as single section if nothing worked
  if (sections.length === 0) {
    return [{
      title: 'Análisis Completo',
      content: cleanText,
      type: 'analysis' as const,
      icon: Sparkles,
      gradient: 'from-indigo-500 to-indigo-600',
      bgGradient: 'from-indigo-50 to-indigo-100'
    }];
  }

  return sections;
};

interface FormattedAIAnalysisProps {
  analysisText: string;
  className?: string;
}

export const FormattedAIAnalysis: React.FC<FormattedAIAnalysisProps> = ({
  analysisText,
  className = ""
}) => {
  const sections = parseAIAnalysis(analysisText);

  if (sections.length === 0) {
    return (
      <div className={`prose prose-sm max-w-none ${className}`}>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{analysisText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header for multiple sections */}
      {sections.length > 1 && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <Sparkles className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold">Informe Ejecutivo de Scouting</h3>
          </div>
          <p className="text-indigo-100 text-sm ml-13">Análisis profesional basado en métricas avanzadas</p>
        </div>
      )}
      
      {sections.map((section, index) => {
        const Icon = section.icon;
        
        return (
          <div
            key={index}
            className={`group relative overflow-hidden bg-gradient-to-br ${section.bgGradient} rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-white/60 hover:border-white/80`}
          >
            {/* Background effects */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-white"></div>
            </div>

            {/* Decorative gradient blob */}
            <div className={`absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br ${section.gradient} rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-300`}></div>

            <div className="relative z-10">
              {/* Header */}
              <div className="flex items-start space-x-4 mb-4">
                <div className={`flex-shrink-0 w-14 h-14 bg-gradient-to-br ${section.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 pt-1">
                  <h4 className="text-lg font-bold text-gray-900 group-hover:text-gray-800 transition-colors leading-tight">
                    {section.title}
                  </h4>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-1">
                    {section.type === 'analysis' && '🎯 Perfil de jugadores'}
                    {section.type === 'conclusion' && '💰 Evaluación económica'}
                    {section.type === 'recommendation' && '📋 Plan de acción'}
                    {section.type === 'insight' && '📊 Datos predictivos'}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-5 border border-white/80 shadow-inner">
                <div className="text-gray-800 leading-relaxed text-sm font-medium">
                  {section.content.split('\n').map((line, i) => {
                    const trimmedLine = line.trim();
                    if (!trimmedLine) return null;

                    // Check if line starts with bullet or dash
                    const isBullet = /^[-•\*]/.test(trimmedLine);
                    const cleanLine = trimmedLine.replace(/^[-•\*]\s*/, '');

                    return (
                      <p key={i} className={`${i > 0 ? 'mt-3' : ''} ${isBullet ? 'pl-4 relative before:content-["→"] before:absolute before:left-0 before:text-gray-400' : ''}`}>
                        {cleanLine}
                      </p>
                    );
                  })}
                </div>
              </div>
              
              {/* Bottom accent */}
              <div className={`mt-4 h-1 bg-gradient-to-r ${section.gradient} rounded-full opacity-40 group-hover:opacity-70 transition-opacity duration-300`}></div>
            </div>
          </div>
        );
      })}
      
      {/* Footer */}
      {sections.length > 1 && (
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="font-semibold">Análisis generado por ScoutMind AI</span>
            <span className="text-gray-400">•</span>
            <span className="text-xs text-gray-500">Basado en {sections.length} dimensiones de análisis</span>
          </div>
        </div>
      )}
    </div>
  );
};